'use client';

import { Suspense, useEffect, useState } from 'react';
import BottomNav from '@/app/components/BottomNav';

interface Exchanger {
  id: number;
  name: string;
  logo: string;
  charge: string;
  rate: string;
  color?: string;
}

function ExchangersContent() {
  const [exchangers, setExchangers] = useState<Exchanger[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const colorMap: { [key: number]: string } = {
    1: 'emerald',
    2: 'blue',
    3: 'purple'
  };

  useEffect(() => {
    const fetchExchangers = async () => {
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/exchangers`);
        if (!response.ok) throw new Error('Failed to fetch exchangers');
        const json = await response.json();
        const exchangersList = json.data || json;
        const exchangersWithColor = exchangersList.map((ex: Exchanger, index: number) => ({
          ...ex,
          color: colorMap[index + 1] || 'blue',
          amount: '100 KWD'
        }));
        setExchangers(exchangersWithColor);
      } catch (err) {
        console.error('Error fetching exchangers:', err);
        setError('Failed to load exchangers');
      } finally {
        setLoading(false);
      }
    };

    fetchExchangers();
  }, []);

  const colorBorderMap: { [key: string]: string } = {
    emerald: 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10',
    blue: 'border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10',
    purple: 'border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10'
  };

  const colorTextMap: { [key: string]: string } = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400'
  };

  if (loading) {
    return <div className="text-center text-slate-400 py-8">Loading exchangers...</div>;
  }

  if (error) {
    return <div className="text-center text-red-400 py-8">{error}</div>;
  }

  return (
    <div className="space-y-3">
      {/* Exchangers List */}
      {exchangers.map((exchanger) => (
          <div
            key={exchanger.id}
            className={`glass-panel p-4 rounded-2xl border transition-all duration-300 cursor-pointer hover:scale-[1.02] ${colorBorderMap[exchanger.color || 'blue']}`}
          >
            <div className="flex items-center justify-between">
              {/* Logo and Name */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  {typeof exchanger.logo === 'string' && exchanger.logo.startsWith('http') ? (
                    <img src={exchanger.logo} alt={exchanger.name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-3xl">{exchanger.logo}</div>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-display font-semibold text-white">{exchanger.name}</h3>
                </div>
              </div>

              {/* Rate and Amount */}
              <div className="text-right">
                <p className="text-xs text-slate-400 mb-1">Rate</p>
                <p className={`text-sm font-display font-semibold ${colorTextMap[exchanger.color || 'blue']}`}>
                  {exchanger.rate}
                </p>
              </div>
            </div>

            {/* Charge and Amount */}
            <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
              <div>
                <p className="text-slate-400 mb-1">Charge</p>
                <p className="text-white font-medium">{exchanger.charge}</p>
              </div>
              <div className="text-right">
                <p className="text-slate-400 mb-1">Amount</p>
                <p className="text-white font-medium">{exchanger.amount}</p>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}

export default function ExchangersPage() {
  return (
    <>
      <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white min-h-screen overflow-x-hidden overflow-y-scroll selection:bg-emerald-500/30 font-sans pt-6 pb-40 px-4 sm:px-6 w-screen">
        <div className="relative z-10">
          {/* Header */}
          <header className="w-full pb-6 flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-display font-semibold tracking-wide text-gradient">Exchange Rates</h1>
              <p className="text-xs text-slate-400 font-medium tracking-wide mt-1">Top companies in Kuwait</p>
            </div>
          </header>

          <main className="w-full pb-10">
            <Suspense fallback={<div className="px-6 py-8 text-center text-slate-400">Loading exchangers...</div>}>
              <ExchangersContent />
            </Suspense>
          </main>
        </div>
      </div>
      <BottomNav />
    </>
  );
}